/**
 * Notification types — per-user inbox layered over the activity feed.
 *
 * The activity feed (see `activity.types.ts`) is the *source of truth*. The
 * notifications service is a per-user **view** over activities that affect a
 * specific user (currently: @-mentions; designed to extend to assignment /
 * status_change with the same pure helper, `computeAffectedUsers`).
 *
 * Read-state lives in its own collection so the activity feed remains
 * append-only and shareable.
 */
/**
 * Given an activity-shaped input, return the set of userIds whose inboxes
 * should surface a new notification. Self-actions are included — if the user
 * @-mentions themself (or self-assigns), they get an inbox entry too. This
 * supports the "note-to-self / reminder" usage pattern.
 *
 * Currently used by `notifications.service` (server-side) and exercised by
 * unit tests in `notifications.service.test.ts`.
 *
 * Implementation kept inline here rather than in the service so the rule is
 * canonical and importable from both packages.
 */
export function computeAffectedUsers(input) {
    const out = new Set();
    switch (input.kind) {
        case 'mention':
        case 'comment': {
            // Every mentioned user is notified.
            for (const m of input.mentions ?? []) {
                if (m.userId)
                    out.add(m.userId);
            }
            break;
        }
        case 'assignment': {
            if (input.assigneeUserId)
                out.add(input.assigneeUserId);
            break;
        }
        case 'status_change': {
            if (input.ownerUserId)
                out.add(input.ownerUserId);
            if (input.assigneeUserId)
                out.add(input.assigneeUserId);
            break;
        }
        case 'attachment': {
            // Owner/assignee are the typical interested parties.
            if (input.ownerUserId)
                out.add(input.ownerUserId);
            if (input.assigneeUserId)
                out.add(input.assigneeUserId);
            break;
        }
    }
    // Self-mentions are intentionally NOT suppressed — the user may @ themself
    // as a reminder/note-to-self, and the bell should reflect that.
    return [...out];
}
/**
 * App-relative path for the entity an activity refers to, or null when the
 * entity has no addressable page.
 *
 * Canonical here rather than in the frontend so email deep links and in-app
 * navigation cannot drift apart. The frontend's `notificationHref` delegates to
 * this; the backend prefixes it with the public base URL.
 */
export function notificationPath(n) {
    switch (n.entityType) {
        case 'submission':
            return `/submissions/${n.entityId}`;
        case 'product':
            return `/products/${n.entityId}`;
        case 'pdlc':
            return `/products/${n.entityId}/pdlc`;
        case 'bug':
            return `/bugs/${n.entityId}`;
        case 'shipment':
            return `/tracking/${n.entityId}`;
        case 'pdlcTask': {
            const productId = typeof n.metadata?.productId === 'string' ? n.metadata.productId : null;
            const drawer = n.metadata?.drawer === 'description' ? 'description' : 'comments';
            if (!productId)
                return null;
            // The PDLC tab has no files drawer and does not list Go Live tasks; the task page has both.
            if (n.metadata?.drawer === 'files' || n.metadata?.goLive === true) {
                return `/products/${productId}/pdlc/tasks/${encodeURIComponent(n.entityId)}`;
            }
            const params = new URLSearchParams({ taskGid: n.entityId, drawer });
            return `/products/${productId}/pdlc?${params.toString()}`;
        }
        case 'marketingRequest':
            return `/marketing/requests?id=${encodeURIComponent(n.entityId)}`;
        case 'fixtureDna':
            return null;
        default:
            return null;
    }
}
/**
 * Project an activity record to a notification for a given recipient. Pure
 * helper; consumed by the service when listing.
 */
export function projectActivityToNotification(activity, userId, read, readAt) {
    return {
        id: activity._id,
        activityId: activity._id,
        userId,
        read,
        readAt: readAt ? readAt.toISOString() : null,
        createdAt: activity.createdAt.toISOString(),
        entityType: activity.entityType,
        entityId: activity.entityId,
        kind: activity.kind,
        authorUserId: activity.authorUserId,
        authorDisplayName: activity.authorDisplayName,
        body: activity.body,
        metadata: activity.metadata,
        // `metadata.mentionedUserIds`: an Asana @mention, belled without the mention email.
        reason: (activity.mentions ?? []).some((m) => m.userId === userId) ||
            (Array.isArray(activity.metadata?.mentionedUserIds) && activity.metadata.mentionedUserIds.includes(userId))
            ? 'mention'
            : 'watch',
    };
}
//# sourceMappingURL=notifications.types.js.map